import clsx from "clsx";
import { t } from "i18next";
import { title } from "process";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Info } from "@/assets/icons/icons";
import { SectionButtonHeader } from "@/components/Interface/SectionButtonHeader/SectionButtonHeader";
import { Tooltip } from "@/components/Interface/Tooltip/Tooltip";
import { ProgressBar } from "@/components/Layout/ProgressBar/ProgressBar";
import {
  RoamingInfoRoamingStatus,
  RoamingInfoUsageZone,
} from "@/services/model";
import {
  formatRoamingTotalInGb,
  formatRoamingUsageInGb,
  isZone1,
} from "@/utils/deviceUtils";
import { Status } from "@/utils/enums";

import { RoamingCallExplanation } from "./RoamingCallExplanation";

type RoamingUsageProps = {
  days: number;
  description: string;
  descriptionExtension?: string;
  initial: number;
  isFirstOfUsageZone: boolean;
  remaining: number;
  consumed?: number;
  title: string;
  isIncludedInService?: boolean;
  roamingStatus?: RoamingInfoRoamingStatus;
  isYearly?: boolean;
  showFlatMobileExplanation?: boolean;
  showRoamingCallExplanation?: boolean;
  isNotAllowedToUse?: boolean;
  shouldTreatAsActive: boolean;
  hasIncludedRoamingCalls: boolean;
  isRoamingCallType?: boolean;
  /** Roaming zone - used to determine display format (GB for Zone 1, MB for Zone 2/3) */
  zone?: RoamingInfoUsageZone | string;
  /** Whether this is displaying data usage (vs minutes or days) */
  isDataUsage?: boolean;
};

const getPercentage = (
  initial: number | undefined,
  current: number | undefined,
) => {
  if (initial !== undefined && current !== undefined) {
    return ((initial - current) / initial) * 100;
  }

  return 0;
};

/** Calculates and displays the data usage of a roaming option */
export const RoamingUsage = ({
  days,
  description,
  descriptionExtension,
  initial,
  isFirstOfUsageZone,
  remaining,
  consumed,
  title,
  isIncludedInService,
  roamingStatus,
  isYearly,
  showFlatMobileExplanation,
  showRoamingCallExplanation,
  isNotAllowedToUse,
  shouldTreatAsActive,
  hasIncludedRoamingCalls,
  isRoamingCallType,
  zone,
  isDataUsage,
}: RoamingUsageProps) => {
  const [usage, setUsage] = useState<number | undefined>(0);
  const { t } = useTranslation("portal");

  // For data usage: Zone 1 displays in GB, Zone 2/3 display in MB
  const useGB = isDataUsage && isZone1(zone);

  const isActive = roamingStatus === Status.active || shouldTreatAsActive;

  useEffect(() => {
    setUsage(isActive ? getPercentage(initial, remaining) : undefined);
  }, [roamingStatus, isActive, initial, remaining]);

  const isUsedUpMobileAboRoaming = isIncludedInService && remaining === 0;
  const isOverspentMobileAboRoaming = isIncludedInService && remaining < 0;
  const isFlatMobileRoamingCallCase =
    isRoamingCallType && hasIncludedRoamingCalls;

  const PackageExplanation = () => {
    if (showFlatMobileExplanation && !isUsedUpMobileAboRoaming) {
      return <MobileAboProRataExplanation title={title} />;
    } else if (showRoamingCallExplanation && !isNotAllowedToUse) {
      return <RoamingCallExplanation title={title} />;
    } else if (showRoamingCallExplanation && isNotAllowedToUse) {
      return (
        <SectionButtonHeader className="mb-4" header={title}>
          <Tooltip
            position="bottom"
            className="right mr-5 mt-4 min-w-[250px] !px-6 !py-4 text-left text-base font-normal text-black sm:mr-6 md:mr-0 md:min-w-[327px]"
            content={
              <p>
                {t(
                  "cockpit.managementTile.roaming.showOption.active.notAllowedTooltip",
                )}
              </p>
            }
          >
            <Info className="h-[21px] w-[21px] text-primary-100" />
          </Tooltip>
        </SectionButtonHeader>
      );
    } else {
      return <p className="mb-4 text-base">{title}</p>;
    }
  };

  return (
    <section className={clsx("mb-4", isUsedUpMobileAboRoaming && "opacity-50")}>
      <PackageExplanation />
      <ProgressBar
        usageBarWidth={
          isNotAllowedToUse ||
          (roamingStatus === RoamingInfoRoamingStatus.ready && consumed === 0)
            ? undefined
            : usage
        }
      />

      <span
        className={clsx(
          "description",
          !isActive && "inactive",
          isOverspentMobileAboRoaming && "!text-red-500",
        )}
      >
        {roamingStatus === "ordered" && (
          <p>{t("cockpit.managementTile.roaming.showOption.ordered.usage")}</p>
        )}

        {roamingStatus === Status.ready && !isFlatMobileRoamingCallCase && (
          <Trans
            components={[<span key="0" className="font-semibold" />]}
            t={t}
            values={{
              days,
            }}
          >
            {isFirstOfUsageZone
              ? "cockpit.managementTile.roaming.showOption.ready.firstPackageOfUsageZone.usage"
              : "cockpit.managementTile.roaming.showOption.ready.notFirstPackageOfUsageZone.usage"}
          </Trans>
        )}
        {(roamingStatus === Status.active || isFlatMobileRoamingCallCase) && (
          <span>
            {isNotAllowedToUse ? (
              <span>
                {t(
                  "cockpit.managementTile.roaming.showOption.active.notAllowed",
                )}
              </span>
            ) : (
              <Trans
                components={[<span key="0" className="font-semibold" />]}
                values={{
                  // If no consumed value is passed it means we also passed different translation text and need to use remaining value.
                  // 0 evaluates as false btw, so we need to check against undefined
                  value: useGB
                    ? formatRoamingUsageInGb(
                        consumed !== undefined ? consumed : remaining,
                      )
                    : consumed !== undefined
                      ? consumed
                      : remaining,
                  initial: useGB ? formatRoamingTotalInGb(initial) : initial,
                }}
                t={t}
              >
                {useGB ? `${description}GB` : description}
              </Trans>
            )}
            {isOverspentMobileAboRoaming && (
              <p className="text-primary-100">
                <Trans
                  components={[
                    <Link
                      key="0"
                      className="underline"
                      to={t(
                        "cockpit.managementTile.roaming.showOption.active.overspentCallsLink",
                      )}
                      target="_blank"
                    />,
                  ]}
                  t={t}
                >
                  {t(
                    "cockpit.managementTile.roaming.showOption.active.overspentCalls",
                  )}
                </Trans>
              </p>
            )}
          </span>
        )}
        {!isYearly && descriptionExtension && (
          <span> {descriptionExtension}</span>
        )}
      </span>
    </section>
  );
};

const MobileAboProRataExplanation = ({ title }: { title: string }) => {
  const { t } = useTranslation("portal");

  return (
    <SectionButtonHeader className="mb-4" header={title}>
      <Tooltip
        position="bottom"
        className="right mr-5 mt-4 min-w-[327px] !px-6 !py-4 text-left text-base font-normal text-black sm:mr-6 md:mr-0"
        content={
          <>
            <h3 className="block pb-2 text-lg font-semibold text-primary-100">
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.title",
              )}
            </h3>
            <ul className="list-disc pl-4">
              <li>
                <b>
                  {t(
                    "cockpit.managementTile.roaming.showOption.flatMobileTooltip.monthlyRenewal.title",
                  )}
                </b>
                <br />
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.monthlyRenewal.description",
                )}
              </li>
              <li>
                <b>
                  {t(
                    "cockpit.managementTile.roaming.showOption.flatMobileTooltip.proRataVolume.title",
                  )}
                </b>
                <br />
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.proRataVolume.description",
                )}
              </li>
            </ul>
            <p className="pt-2">
              <b>
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.example.title",
                )}
              </b>
              <br />
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.example.description",
              )}
            </p>
          </>
        }
      >
        <Info className="h-[21px] w-[21px] text-primary-100" />
      </Tooltip>
    </SectionButtonHeader>
  );
};
